import type { ViewerModel } from './viewer-model/ViewerModel'
import type { IBaseComponent } from './baseComponent'
import { FetchFn } from '../features/fetch/interfaces'

export type { RendererType } from '@wix/thunderbolt-ssr-api'

export type LogTypes = 'log' | 'info' | 'warn' | 'error' | 'debug'

export type ReleaseWorker = () => void

export type CachePolicy = {
	ttlDays: number
	rules: Array<CachePolicyRule>
}

type MaybePromise<T> = T | Promise<T>

export type CacheAPI = {
	/**
	 * Calls fetch and store successful response data under the provided key in the cache, fetch call is skipped if key already
	 * contains a cached value
	 *
	 * @param key key to store successful response at
	 * @param fetchArgs fetch function arguments
	 */
	fetch?: (key: string, ...fetchArgs: Parameters<typeof fetch>) => ReturnType<FetchFn>
	get<T>(key: string): MaybePromise<T> | null | undefined
	set(key: string, data: any): void
	invalidate(key: string): void
	invalidateAll(): void
}

export type CachePolicyRule = {
	name: string
	url: string
	method: RequestInit['method']
	action: 'deny' | 'allow'
}

export type CacheRecord = {
	ttl: number
	dateCreated: number
	url: string
	headers: Record<string, string>
	body: string
	ok?: boolean
	type?: ResponseType
	status?: number
	statusText?: string
	redirected?: boolean
}

export interface IReleaseWorkerApi {
	releaseWorker: ReleaseWorker
}

export interface IWebViewToReactNativeApi {
	reportError: PreViewerBiReporter['reportError']
	log(type: LogTypes, args: Array<any>): void
	onReady(createWebWorkerApi: (...args: Array<any>) => IReleaseWorkerApi & unknown): void // webView doesn't know how createWebWorkerApi looks like apart from it returning an object with releaseWorker api
	fetchResponseObject?: (url: string, options: any) => Promise<ProxiedFetchResponseData>
	fetch?: (url: string, options: any) => Promise<string>
	getFromCache?: (key: string) => MaybePromise<CacheRecord> | null | undefined
	setInCache?: (key: string, data: any) => void
	invalidateCacheRecord?: (key: string) => void
	invalidateAllCache?: () => void
}

export type ISyncCreateWebWorkerApi<T = any> = (webWorkerUrl: string) => IReleaseWorkerApi & T
export type ICreateWebWorkerApi<T = any> = (webWorkerUrl: string) => Promise<IReleaseWorkerApi & T>

// TODO import type { AppProps } from 'feature-react-renderer'

export type RendererManifest = {
	bootstrap: Array<string>
	main: string
	entries: {
		[entry: string]: string
	}
}

export type RendererAuthentication = {
	getVeloInstance: () => Promise<string>
	getMetasiteInstance?: () => Promise<string>
}

export type RendererProps = {
	waitForDomReady?: () => Promise<void>
	rendererParams: RendererParams
	environment: any
	componentLoaders: any | Promise<any>
	bi: {
		reportBi: boolean
		vsi: string
		receivedModelIdTimestamp: number
		overrideParams?: Record<string, any>
		appNameOverride?: string
	}
	sentry?: {
		dsn: string
		extraTags: Record<string, string>
	}
	headers: Record<string, string>
	rootCompId: string
	BaseComponent: IBaseComponent
	createWebWorkerApi: ICreateWebWorkerApi
	finishedRenderingCallback: () => void
	extensions?: Record<string, any>
	authentication?: RendererAuthentication
}

export type WixViewer = {
	initViewer: (args: RendererProps) => Promise<ReactRenderer>
}

export type RendererParams = {
	viewerVersion: string
	viewerModel: ViewerModel
	cachePolicy: CachePolicy
	manifest: RendererManifest
}

export type ReactRenderer = {
	render: () => JSX.Element
	releaseViewer: () => void
}

export type ProxiedFetchResponseData = {
	headers: Record<string, string>
	body: string
	ok: boolean
	type: ResponseType
	status: number
	statusText: string
	redirected: boolean
}

type NumericBoolean = 0 | 1

export type BiParams = {
	isRollout: NumericBoolean
	isSavRollout: NumericBoolean
	isDacRollout: NumericBoolean
	dc: number | string
	msid: string
	sessionId: string
	remoteViewerVersion: string
	mobuiVersion: string
	renderer_lib_version: string
	siteType: number
	platformOnSite: boolean
	isCached: boolean
	caching: string
}

export type PreViewerBiReporter = {
	updateBiParams(biParams: Partial<BiParams>): void
	reportError(errorType: string, errorInfo?: string): void
	measureInteraction<T>(name: string, fn: () => T): T
	measurePhase<T>(name: string, fn: () => T): T
	reportRendererStart(): void
	sendBI(evid: EventId, extra: Record<string, any>): void
}

export enum EventId {
	LOAD_PHASE_START = 28,
	LOAD_PHASE_FINISH = 22,
	INTERACTION_START = 24,
	INTERACTION_END = 25,
	ERROR = 26,
}
